import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useAppData } from "../../../context/AppDataContext";
import TaskTable from "../../task/TaskTable";
import TaskKanban from "../../task/TaskKanban";
import TaskDetailDrawer from "../../task/TaskDetailDrawer";
import FilePreview from "../../files/FilePreview.jsx";
import FormFieldTags from "../../input/FormFieldTags";
import ConfirmDelete from "../../modals/ConfirmDelete";
import AddToSprint from "../../../command-bar/AddToSprint.jsx";
import StatusDropdown from "../../dropdown/StatusDropdown.jsx";
import { useAuthInfo } from "../../../context/AuthContext.jsx";
import Loading from "../../layout/Loading.jsx";

import NovelEditor from "../../core/NovelEditor";
import Modal from "../../modals/Modal.jsx";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useSearchTerm from "../../../hooks/useSearchTerm.js";
import useDebounce from "../../../hooks/useDebounce.js";
import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall.js";

import SearchInput from "../../input/SearchInput.jsx";
import {
  faFolder,
  faEllipsisV,
  faChevronDown,
  faChevronUp,
  faList,
  faColumns,
} from "@fortawesome/free-solid-svg-icons";
import DragDrop from "../../DragDrop.jsx";
import { setCachedBreadcrumbLabel } from "../../../helpers/breadcrumbCache";
import { appendBreadcrumbSearch } from "../../../helpers/breadcrumbNavigation";
import ColorPicker from "../../core/ColorPicker.jsx";
import {
  errorToast,
  successfulToast,
} from "../../../util/toastNotifications.jsx";

const sampleFiles = [
  {
    name: "Wireframe UI Kit.zip",
    size: "5.8 MB",
    url: "#",
  },
  {
    name: "Brand Styles Guide.pdf",
    size: "487 KB",
    url: "#",
  },
];

const ProjectDetailPage = () => {
  const [searchTerm, setSearchTerm] = useSearchTerm();

  const { projectId } = useParams();
  const history = useHistory();
  const { api } = useAppData();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isUploadCollapsed, setIsUploadCollapsed] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [description, setDescription] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const originalDescriptionRef = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState(null);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const isTaskMenuOpen = viewMode === "list";
  const [resetSelectionKey, setResetSelectionKey] = useState(0);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [isCompleteSelected, setIsCompleteSelected] = useState(false);
  const [filterParameter, setFilterParameter] = useState("Backlog");
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [selectedTaskTitle, setSelectedTaskTitle] = useState(null);
  const searchDebounce = useDebounce(searchTerm);

  const {
    fetchData: fetchTasks,
    data: tasksData,
    isLoading: tasksLoading,
  } = useTriggeredAPICall();

  const { userInfo } = useAuthInfo();

  const tasksToDisplay = filteredTasks ?? tasks;

  const openPreview = (file) => {
    setPreviewFile(file);
  };
  const closePreview = () => {
    if (previewFile?.previewUrl) {
      URL.revokeObjectURL(previewFile.previewUrl);
    }
    setPreviewFile(null);
  };

  const handleStatusChange = async (statusId) => {
    try {
      await api.project.updateProject(projectId, {
        status_id: statusId,
        version: project.version,
      });
      const updatedProject = await api.project.getProjectById(projectId);
      setProject(updatedProject);
      setShowStatusDropdown(false);
    } catch (error) {
      console.error("Error updating project status:", error);
      errorToast("Failed to update project status. Please try again.");
    }
  };

  const handleStatusToggle = () => {
    setShowStatusDropdown((prev) => !prev);
  };

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!api?.project || !api?.taskStatus) {
        return;
      }
      try {
        setLoading(true);
        const [projectData, statusesData, projectStatusesData] =
          await Promise.all([
            api.project.getProjectById(projectId),
            api.taskStatus.getTaskStatuses(),
            api.projectStatus.getProjectStatuses(),
          ]);
        setProject(projectData);
        setTaskStatuses(statusesData);
        setProjectStatuses(projectStatusesData);

        const normalized = (projectData.files ?? []).map((f) => ({
          ...f,
          preview_url: f.preview_url ?? f.file_url ?? null,
        }));
        setUploadedFiles(normalized);

        if (projectData?.description) {
          try {
            const parsed = JSON.parse(projectData.description);
            setDescription(parsed);
            originalDescriptionRef.current = projectData.description;
          } catch {
            setDescription(null);
            originalDescriptionRef.current = null;
          }
        } else {
          setDescription(null);
          originalDescriptionRef.current = null;
        }
      } catch (error) {
        console.error("Error fetching project data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [api, projectId]);

  useEffect(() => {
    if (project?.project_id && project?.name) {
      setCachedBreadcrumbLabel("project", project.project_id, project.name);
    }
  }, [project]);

  const isBacklog = Boolean(filterParameter === "Backlog");

  const debouncedDescription = useDebounce(description, 1000);

  const saveDescription = async () => {
    const descriptionString = JSON.stringify(debouncedDescription);

    if (descriptionString === originalDescriptionRef.current) {
      return;
    }

    try {
      setIsSavingDescription(true);
      await api.project.updateProject(projectId, {
        description: descriptionString,
      });
      const updatedProject = await api.project.getProjectById(projectId);
      setProject(updatedProject);
      originalDescriptionRef.current = descriptionString;
    } catch (error) {
      console.error("Error updating project description:", error);
    } finally {
      setIsSavingDescription(false);
    }
  };

  useEffect(() => {
    if (projectId && !isDrawerOpen) {
      const endpoint = searchDebounce
        ? `/tasks/search?q=${encodeURIComponent(
            searchDebounce
          )}&project_id=${projectId}&sort=priority&sort_dir=asc`
        : `/tasks/project/${projectId}?sort=priority&sort_dir=asc`;

      fetchTasks(endpoint);
    }
  }, [searchDebounce, projectId, fetchTasks, isDrawerOpen]);

  useEffect(() => {
    if (tasksData?.results) {
      setTasks(tasksData.results);

      if (filterParameter === "Backlog") {
        setFilteredTasks(
          tasksData.results.filter((task) => task.status === "New")
        );
      } else if (filterParameter === "Current") {
        setFilteredTasks(
          tasksData.results.filter(
            (task) =>
              task.status !== "New" &&
              task.status !== "Complete" &&
              task.status !== "Awaiting QA" &&
              task.status !== "QA"
          )
        );
      } else if (filterParameter === "QA") {
        setFilteredTasks(
          tasksData.results.filter(
            (task) => task.status === "Awaiting QA" || task.status === "QA"
          )
        );
      } else if (filterParameter === "Complete") {
        setFilteredTasks(
          tasksData.results.filter((task) => task.status === "Complete")
        );
      }
    }
  }, [tasksData, filterParameter]);

  const handleTagRemove = async (tagId) => {
    try {
      await api.project.toggleTag(projectId, tagId);
      const updatedTags = (project.tags || []).filter(
        (tag) => tag.tag_id !== tagId
      );
      setProject((prev) => ({
        ...prev,
        tags: updatedTags,
      }));
    } catch (error) {
      console.error("Error removing tag from project:", error);
    }
  };

  const handleAddTagToObject = async (tagId) => {
    try {
      await api.project.toggleTag(projectId, tagId);

      const existingTag = (project.tags || []).find(
        (tag) => tag.tag_id === tagId
      );
      if (existingTag) {
        return;
      }

      const tagResponse = await api.tag.getTagById(tagId);
      const newTag = {
        tag_id: tagResponse.tag_id,
        name: tagResponse.name,
        color: tagResponse.color,
      };

      setProject((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), newTag],
      }));
    } catch (error) {
      console.error("Error adding tag to project:", error);
    }
  };

  const handleFileToggle = async (fileId, newFile = false) => {
    try {
      await api.project.toggleFile(projectId, fileId);

      if (!newFile) {
        const updatedFiles = (project.files || []).filter(
          (file) => file.file_id !== fileId
        );

        setProject((prev) => ({
          ...prev,
          files: updatedFiles,
        }));

        setUploadedFiles(updatedFiles);
      } else {
        setProject((prev) => ({
          ...prev,
          files: [...(prev.files || []), newFile],
        }));

        setUploadedFiles((prev) => [...prev, newFile]);
      }
    } catch (error) {
      console.error("Error toggling file:", error);
    }
  };

  const handleTaskStatusChange = async (taskId, taskStatusId) => {
    const status = taskStatuses.find((s) => s.task_status_id === taskStatusId);

    if (!status) {
      console.error("Status not found:", taskStatusId);
      return;
    }

    const currentTask = tasks.find((t) => t.task_id === taskId);

    if (!currentTask) {
      console.error("Task not found:", taskId);
      return;
    }

    const originalTasks = tasks;

    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.task_id === taskId
          ? {
              ...task,
              status: status.name,
              status_color: status.color,
              status_id: status.task_status_id,
            }
          : task
      )
    );

    try {
      const response = await api.task.updateTask(taskId, {
        status_id: status.task_status_id,
        version: currentTask.version,
      });

      if (response) {
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.task_id === taskId
              ? {
                  ...task,
                  status: response.status,
                  status_color: response.status_color,
                  status_id: response.status_id,
                  version: response.version,
                }
              : task
          )
        );
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      setTasks(originalTasks);
    }
  };

  const selectedTasksInfo = tasks.filter((t) =>
    selectedTasks.includes(t.task_id)
  );

  const handleMergeTasks = async () => {
    try {
      const primaryTaskId = selectedTaskTitle;
      const secondaryTask = selectedTasksInfo.find(
        (t) => t.task_id !== primaryTaskId
      );
      const secondaryTaskId = secondaryTask.task_id;
      const data = {
        primary_task: primaryTaskId,
        secondary_task: secondaryTaskId,
      };
      await api.task.mergeTasks(data);
      successfulToast("Task merged seccessfully.");
      setIsMergeModalOpen(false);
      const endpoint = `/tasks/project/${projectId}`;
      await fetchTasks(endpoint);
    } catch (error) {
      const message = error?.message || "Failed to merge tasks";
      errorToast(message);
    } finally {
      setResetSelectionKey();
    }
  };

  const handleProjectFileUpload = async ({ file, preview_url }) => {
    const isDuplicate = uploadedFiles.some(
      (existingFile) =>
        existingFile.file_name === file.name &&
        existingFile.file_size === file.size
    );

    if (isDuplicate) {
      alert(`"${file.name}" has already been uploaded to this project.`);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("project_id", projectId);
    formData.append("org_id", project.orgId);

    try {
      const response = await api.file.createFiles(formData);
      const uploadedFile = response.results;

      const newFile = { ...uploadedFile, preview_url };

      setProject((prev) => ({
        ...prev,
        files: [...(prev.files ?? []), newFile],
      }));
      setUploadedFiles((prev) => [...prev, newFile]);
      successfulToast(`${file.name} uploaded successfully!`);
    } catch (err) {
      if (err?.status === 400) {
        errorToast(
          `"${file.name}" could not be uploaded. The file type may not be supported.`
        );
      } else {
        errorToast(`Failed to upload "${file.name}". Please try again.`);
      }
    }
  };

  const handleRemoveFile = async (file) => {
    try {
      await handleFileToggle(file.file_id);
      if (file.previewUrl) {
        URL.revokeObjectURL(file.preview_url);
      }
      successfulToast("File removed successfully");
    } catch (err) {
      console.error("Failed to delete file", err);
    }
  };

  const handleColor = async (e) => {
    try {
      await api.project.updateProject(projectId, { color: e });
      const updatedProject = await api.project.getProjectById(projectId);
      setProject(updatedProject);
    } catch (error) {
      console.error("Error updating project color:", error);
    }
  };

  const handleTasksChange = (updatedTasks) => {
    setTasks(updatedTasks);
  };

  const toggleDetails = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleTaskClick = (taskId) => {
    setSelectedTaskId(taskId);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTaskId(null);
  };

  const handleEditProject = () => {
    setIsDropdownOpen(false);
    history.push(`/project-form/${projectId}`);
  };

  if (loading) {
    return (
      <div className="project-detail-loading">
        <Loading />
      </div>
    );
  }

  if (!project) {
    return <div>Project not found</div>;
  }

  const currentStatusId =
    project.status_id ||
    project.status?.project_status_id ||
    project.status?.status_id ||
    null;
  const currentStatus = currentStatusId
    ? projectStatuses.find(
        (status) => status.project_status_id === currentStatusId
      )
    : null;
  const statusDisplayName =
    typeof project.status === "object" ? project.status?.name : project.status;
  const statusColor = project.status_color || currentStatus?.color;

  const tasksInBacklog = tasks.filter(
    (task) => task.status?.name !== "Complete" && !task.parent_task_id
  ).length;

  const tasksCompleted = tasks.filter(
    (task) => task.status?.name === "Complete"
  ).length;

  const tasksInProgress = tasks.filter(
    (task) =>
      task.status?.name &&
      task.status.name !== "New" &&
      task.status.name !== "Complete"
  ).length;

  return (
    <div className="width-full max-width-full">
      <div className="detail-header">
        <div className="header-main">
          <h1
            style={{
              borderBottom: `2px solid ${project.color}`,
              display: "flex",
            }}
          >
            <ColorPicker
              initialColor={project.color}
              handleColor={handleColor}
              display={<FontAwesomeIcon icon={faFolder} className="mr-2" />}
            />
            {project.name}
          </h1>
          {userInfo.role.name !== "user" && (
            <div className="action-buttons mt-4">
              <FontAwesomeIcon
                icon={faEllipsisV}
                className="dropdown-toggle"
                data-bs-toggle="dropdown"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              />
              {isDropdownOpen && (
                <div className="dropdown-menu show">
                  <button className="mr-2" onClick={handleEditProject}>
                    Edit Project
                  </button>
                  <ConfirmDelete objectType="project" id={projectId} />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="header-tags">
          <FormFieldTags
            onClick={(e) => e.stopPropagation()}
            selectedTags={project.tags || []}
            handleTagRemove={handleTagRemove}
            handleAddTagToObject={handleAddTagToObject}
            objectType="projects"
            objectId={projectId}
            objectPk="project_id"
            hideLabel={true}
          />
        </div>

        <div className="header-info">
          <div className="info-item">
            <span className="info-label">Company</span>
            <span className="info-value">
              {project.company ? (
                <a href={`/company/${project.company.company_id}`}>
                  {project.company.name}
                </a>
              ) : (
                "N/A"
              )}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Project Manager</span>
            <span className="info-value">
              {project.project_manager ? (
                <a href={`/user/${project.project_manager.user_id}`}>
                  {project.project_manager.first_name}{" "}
                  {project.project_manager.last_name}
                </a>
              ) : (
                "N/A"
              )}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Product Owner</span>
            <span className="info-value">
              {project.product_owner ? (
                <a href={`/user/${project.product_owner.user_id}`}>
                  {project.product_owner.first_name}{" "}
                  {project.product_owner.last_name}
                </a>
              ) : (
                "N/A"
              )}
            </span>
          </div>

          {project.due_date && (
            <div className="info-item">
              <span className="info-label">Due Date</span>
              <span className="info-value">
                {new Date(project.due_date).toLocaleDateString()}
              </span>
            </div>
          )}

          <div className="info-item">
            <span className="info-label">Status</span>
            <span className="info-value">
              <StatusDropdown
                statuses={projectStatuses}
                currentStatusId={currentStatusId}
                statusLabel={statusDisplayName}
                statusColor={statusColor}
                showDropdown={showStatusDropdown}
                onToggle={handleStatusToggle}
                onClose={() => setShowStatusDropdown(false)}
                onSelect={handleStatusChange}
                assignee={Boolean(project.project_manager)}
                statusIdKey="project_status_id"
              />
            </span>
          </div>
        </div>
      </div>

      <div className="card mb-4 detail-header detail-expanded ">
        <h3 className=" d-flex pb-4">
          DETAILS
          <span
            className="ml-2 chevron-toggle"
            onClick={() => {
              setIsDetailOpen(!isDetailOpen);
            }}
          >
            <FontAwesomeIcon
              icon={isDetailOpen ? faChevronUp : faChevronDown}
            />
          </span>
        </h3>
        {isDetailOpen && (
          <div>
            <div className="info-item">
              <span className="info-label">
                Description
                {isSavingDescription && (
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "0.875rem",
                      color: "#666",
                    }}
                  >
                    Saving...
                  </span>
                )}
              </span>
              <div className="info-value description-textarea">
                <NovelEditor
                  initialValue={description}
                  onChange={setDescription}
                  onBlur={saveDescription}
                />
              </div>
            </div>

            <div className="expanded-info">
              <div className="info-item">
                <span className="info-label">Created By</span>
                <span className="info-value">
                  {project.created_by ? (
                    <a href={`/user/${project.created_by.user_id}`}>
                      {project.created_by.first_name}{" "}
                      {project.created_by.last_name}
                    </a>
                  ) : (
                    "N/A"
                  )}
                </span>
              </div>

              <div className="info-item">
                <span className="info-label">Start Date</span>
                <span className="info-value">
                  {new Date(project.start_date).toLocaleDateString()}
                </span>
              </div>

              <div className="info-item">
                <span className="info-label">Total Hours</span>
                <span className="info-value">{project.total_hours || "0"}</span>
              </div>

              <div className="info-item">
                <span className="info-label">Billable Hours</span>
                <span className="info-value">
                  {project.billable_hours || "0"}
                </span>
              </div>

              <div className="info-item">
                <span className="info-label">Non-billable Hours</span>
                <span className="info-value">
                  {project.non_billable_hours || "0"}
                </span>
              </div>

              <div className="info-item">
                <span className="info-label">Tasks in Backlog</span>
                <span className="info-value">{tasksInBacklog}</span>
              </div>

              <div className="info-item">
                <span className="info-label">Tasks Completed</span>
                <span className="info-value">{tasksCompleted}</span>
              </div>

              <div className="info-item">
                <span className="info-label">Tasks in Progress</span>
                <span className="info-value">{tasksInProgress}</span>
              </div>

              <div className="info-item">
                <span className="info-label">Invoiced</span>
                <span className="info-value">$ {tasksInProgress}.00</span>
              </div>

              <div className="info-item">
                <span className="info-label">Received</span>
                <span className="info-value">$ {tasksInProgress}.00</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="card mb-4 detail-header">
        <h3 className=" d-flex">
          Files
          <span
            className="ml-2 chevron-toggle"
            onClick={() => {
              setIsUploadCollapsed(!isUploadCollapsed);
            }}
          >
            <FontAwesomeIcon
              icon={isUploadCollapsed ? faChevronDown : faChevronUp}
            />
          </span>
        </h3>
        {!isUploadCollapsed && (
          <div className="detail-expanded">
            {userInfo?.role?.name !== "user" && (
              <div>
                <DragDrop
                  onSuccessfulUpload={handleProjectFileUpload}
                  accept="*/*"
                  multiple={true}
                  maxSize={20_000_000}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                />
              </div>
            )}

            <div>
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                  {userInfo?.role?.name !== "user" ? (
                    <p className="file-upload-message">
                      File{uploadedFiles.length > 1 ? "s" : ""} Uploaded
                      Successfully:
                    </p>
                  ) : (
                    <p className="uploaded-files-message">
                      Uploaded File{uploadedFiles.length > 1 ? "s" : ""}:
                    </p>
                  )}
                  <ul>
                    {uploadedFiles.map((file) => (
                      <li key={file.file_id} className="file-display-wrapper">
                        <span
                          className="file-name"
                          onClick={() => openPreview(file)}
                        >
                          {file.file_name}
                        </span>

                        {userInfo?.role?.name !== "user" && (
                          <button
                            className="remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(file);
                            }}
                          >
                            x
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div>
              <FilePreview file={previewFile} onClose={closePreview} />
            </div>
          </div>
        )}
      </div>
      <div
        className="card page-container width-full"
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: "0",
        }}
      >
        <div
          className="left-column"
          style={{
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <div
            className="project-tasks"
            style={{
              width: "100%",
              maxWidth: "100%",
            }}
          >
            <div className="tasks-header">
              <div className="button-row my-0">
                <h3>Backlog Tasks</h3>
                <div className="flex-row-no-width gap-10">
                  {isBacklog && (
                    <button
                      className="my-0"
                      disabled={selectedTasks.length !== 2}
                      onClick={() => {
                        setIsMergeModalOpen(true);
                      }}
                    >
                      Merge Tasks
                    </button>
                  )}
                  <button
                    className="my-0"
                    onClick={() =>
                      history.push(
                        appendBreadcrumbSearch(
                          `/task-form?project_id=${projectId}`,
                          {
                            parentType: "project",
                            parentId: projectId,
                            parentLabel: project?.name,
                          }
                        )
                      )
                    }
                  >
                    + New Task
                  </button>
                </div>
              </div>
              <div className="flex-rows backlog-task-header">
                <div className="view-filter-wrapper">
                  <SearchInput
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="width-200px"
                  />
                  <button
                    className={`tab-button ${
                      viewMode === "kanban" ? "active" : ""
                    }`}
                    onClick={() => {
                      setFilterParameter(null);
                      setViewMode("kanban");
                    }}
                  >
                    <FontAwesomeIcon icon={faColumns} /> Kanban
                  </button>
                  <button
                    className={`tab-button mr-2 ${
                      viewMode === "list" ? "active" : ""
                    }`}
                    onClick={() => {
                      setViewMode("list");
                      setFilterParameter("Backlog");
                    }}
                  >
                    <FontAwesomeIcon icon={faList} /> List
                  </button>
                  {isTaskMenuOpen && (
                    <div className="task-type-wrapper">
                      <button
                        className={`tab-button ${
                          filterParameter === "Backlog" ? "selected" : ""
                        }`}
                        onClick={() => {
                          setIsCompleteSelected(false);
                          setFilterParameter("Backlog");
                        }}
                      >
                        Backlog
                      </button>

                      <button
                        className={`tab-button ${
                          filterParameter === "Current" ? "selected" : ""
                        }`}
                        onClick={() => {
                          setViewMode("list");
                          setIsCompleteSelected(false);
                          setFilterParameter("Current");
                        }}
                      >
                        Current Tasks
                      </button>
                      <button
                        className={`tab-button ${
                          filterParameter === "QA" ? "selected" : ""
                        }`}
                        onClick={() => {
                          setViewMode("list");
                          setIsCompleteSelected(false);
                          setFilterParameter("QA");
                        }}
                      >
                        QA
                      </button>
                      <button
                        className={`tab-button ${
                          filterParameter === "Complete" ? "selected" : ""
                        }`}
                        onClick={() => {
                          setIsCompleteSelected(true);
                          setViewMode("list");
                          setFilterParameter("Complete");
                        }}
                      >
                        Completed
                      </button>
                    </div>
                  )}
                </div>
                {isTaskMenuOpen && (
                  <div>
                    <AddToSprint
                      selectedTasks={selectedTasks}
                      setSelectedTasks={setSelectedTasks}
                      resetSelection={() =>
                        setResetSelectionKey((prev) => prev + 1)
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            {viewMode === "list" ? (
              <TaskTable
                data={tasksToDisplay}
                loading={tasksLoading}
                onDataChange={handleTasksChange}
                onTaskClick={handleTaskClick}
                showToggleBtn={!isCompleteSelected}
                onSelectionChange={setSelectedTasks}
                resetSelectionKey={resetSelectionKey}
              />
            ) : (
              <div className="kanban-scroll-container">
                <TaskKanban
                  tasks={tasks}
                  loading={tasksLoading}
                  onStatusChange={handleTaskStatusChange}
                  onTaskClick={handleTaskClick}
                  taskStatuses={taskStatuses}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isModalOpen={isMergeModalOpen}
        onRequestClose={() => setIsMergeModalOpen(false)}
        overlay={{
          zIndex: 2000,
        }}
        content={{
          height: "265px",
          width: "400px",
          borderRadius: "15px",
        }}
      >
        <div className="merge-modal-wrapper flex-columns flex-align-center">
          <h2 className="pb-5">Please Select the title</h2>
          <div className="flex-columns flex-justify-center flex-align-center pb-5">
            {selectedTasksInfo.map((t) => (
              <label key={t.task_id} className="pb-4">
                <input
                  type="radio"
                  name="mergeTitle"
                  value={t.task_id}
                  checked={selectedTaskTitle === t.task_id}
                  onChange={() => setSelectedTaskTitle(t.task_id)}
                />
                {t.name}
              </label>
            ))}
          </div>
        </div>
        <div className="button-wrapper flex-rows flex-space-between">
          <button
            className="cancel-button"
            onClick={() => setIsMergeModalOpen(false)}
          >
            Cancel
          </button>
          <button
            onClick={() => handleMergeTasks()}
            disabled={!selectedTaskTitle}
          >
            Merge
          </button>
        </div>
      </Modal>

      <TaskDetailDrawer
        taskId={selectedTaskId}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        breadcrumbParent={{
          parentType: "project",
          parentId: projectId,
          parentLabel: project?.name,
        }}
      />
    </div>
  );
};

export default ProjectDetailPage;
